import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, Plus, X, Search, Loader2, AlertTriangle, PackageX, Phone, Mail, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const CONDITION_STYLES = {
  NE: "bg-emerald-100 text-emerald-700",
  FN: "bg-emerald-100 text-emerald-700",
  NS: "bg-teal-100 text-teal-700",
  OH: "bg-sky-100 text-sky-700",
  SV: "bg-blue-100 text-blue-700",
  RP: "bg-amber-100 text-amber-700",
  AR: "bg-orange-100 text-orange-700",
};

function ConditionChip({ code }) {
  if (!code) return <span className="text-gray-300">—</span>;
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded font-mono text-[10px] font-semibold ${CONDITION_STYLES[code] || "bg-gray-100 text-gray-600"}`}>
      {code}
    </span>
  );
}

function fmtPrice(p, currency) {
  if (p === null || p === undefined || p === "" || Number(p) === 0) return null;
  const n = Number(p);
  if (Number.isNaN(n)) return String(p);
  return `${n.toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? ` ${currency}` : ""}`;
}

export default function SearchEngine() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const hasAccess = isAdmin || !!user?.search_engine_access;

  const [pns, setPns] = useState([""]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const setPn = (i, v) => setPns((list) => list.map((x, j) => (j === i ? v : x)));
  const addPn = () => setPns((list) => (list.length >= 10 ? list : [...list, ""]));
  const removePn = (i) => setPns((list) => (list.length > 1 ? list.filter((_, j) => j !== i) : [""]));

  const runSearch = async () => {
    const clean = pns.map((p) => p.trim()).filter(Boolean);
    if (!clean.length) { toast.error("Enter at least one part number"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/stores/search-engine/search", { part_numbers: clean });
      setResults(data.results || []);
      const found = (data.results || []).reduce((s, r) => s + (r.total || 0), 0);
      const ils = (data.results || []).reduce((s, r) => s + (r.ils?.parts_total || 0), 0);
      toast.success(`${found} PartsBase · ${ils} ILS listing${found + ils === 1 ? "" : "s"} found`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="se-no-access">
        <Globe className="w-10 h-10 mx-auto text-gray-300" />
        <h1 className="font-display text-2xl font-semibold mt-4">Search Engine</h1>
        <p className="text-sm text-gray-500 mt-2">Ask an admin to grant you Search Engine access.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-8" data-testid="search-engine-page">
      <div className="mb-6">
        <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Stores · PartsBase Marketplace</div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight mt-1">Search Engine</h1>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* ---------------- Left: search panel ---------------- */}
        <div className="w-full lg:w-72 shrink-0 lg:sticky lg:top-20 border border-summit-hairline rounded-lg bg-white" data-testid="se-search-panel">
          <div className="px-4 py-3 border-b border-summit-hairline bg-summit-glacier/50">
            <span className="font-display text-sm font-medium tracking-tight flex items-center gap-2">
              <Search className="w-4 h-4 text-summit-primary" /> Part number search
            </span>
          </div>
          <div className="p-4 space-y-2">
            {pns.map((p, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <Input
                  value={p}
                  onChange={(e) => setPn(i, e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
                  placeholder={`Part number ${i + 1}`}
                  className="h-9 font-mono text-sm uppercase"
                  data-testid={`se-pn-input-${i}`}
                />
                <button
                  type="button"
                  onClick={() => removePn(i)}
                  className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50"
                  aria-label="Remove part number"
                  data-testid={`se-remove-pn-${i}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addPn}
              disabled={pns.length >= 10}
              className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-summit-primary hover:text-summit-primary-soft disabled:opacity-40"
              data-testid="se-add-pn"
            >
              <Plus className="w-3.5 h-3.5" /> Add part number
            </button>
            <Button
              className="w-full bg-summit-primary hover:bg-summit-primary-soft mt-2"
              onClick={runSearch}
              disabled={loading}
              data-testid="se-search-btn"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Searching…</>) : (<><Search className="w-4 h-4 mr-1.5" /> Search</>)}
            </Button>
            <p className="font-mono text-[9px] uppercase tracking-widest text-gray-400 pt-1">
              Up to 10 part numbers · PartsBase + ILS
            </p>
          </div>
        </div>

        {/* ---------------- Right: results ---------------- */}
        <div className="flex-1 min-w-0 w-full" data-testid="se-results">
          {!results && !loading && (
            <div className="border border-dashed border-summit-hairline rounded-lg p-16 text-center" data-testid="se-empty-state">
              <Globe className="w-10 h-10 mx-auto text-gray-300" />
              <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-gray-400">
                Enter part numbers on the left to search the PartsBase marketplace
              </p>
            </div>
          )}
          {loading && !results && (
            <div className="border border-summit-hairline rounded-lg p-16 text-center">
              <Loader2 className="w-8 h-8 mx-auto text-summit-primary animate-spin" />
              <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-gray-400">Searching PartsBase…</p>
            </div>
          )}
          {results && results.map((group) => (
            <div key={group.part_number} className="mb-6 border border-summit-hairline rounded-lg bg-white overflow-hidden" data-testid={`se-group-${group.part_number}`}>
              <div className="px-4 py-3 border-b border-summit-hairline bg-summit-glacier/50 flex items-center gap-2 flex-wrap">
                <span className="font-mono text-sm font-semibold text-summit-ink">{group.part_number}</span>
                <span className="inline-flex px-2 py-0.5 rounded-full bg-summit-primary/10 text-summit-primary font-mono text-[10px] uppercase tracking-widest">
                  PartsBase · {group.total}
                </span>
                {group.total > group.items.length && (
                  <span className="font-mono text-[10px] text-gray-400">showing first {group.items.length}</span>
                )}
              </div>

              {/* ILS marketplace — live availability counts + deep link to full results. */}
              {group.ils && (
                <div className="px-4 py-2.5 border-b border-summit-hairline bg-sky-50/60 flex items-center gap-3 flex-wrap" data-testid={`se-ils-${group.part_number}`}>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-sky-700 font-semibold">
                    <Globe className="w-3.5 h-3.5" /> ILS
                  </span>
                  {group.ils.error ? (
                    <span className="text-xs text-rose-600 flex items-center gap-1" data-testid={`se-ils-error-${group.part_number}`}>
                      <AlertTriangle className="w-3.5 h-3.5" /> {group.ils.error}
                    </span>
                  ) : group.ils.counts && group.ils.counts.length ? (
                    <>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {group.ils.counts.map((c) => (
                          <span key={c.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-sky-200 text-sky-800 font-mono text-[10px]">
                            <span className="font-semibold">{c.count.toLocaleString()}</span> {c.label}
                          </span>
                        ))}
                      </div>
                      <a
                        href={group.ils.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-sky-700 hover:text-sky-900"
                        data-testid={`se-ils-link-${group.part_number}`}
                      >
                        View on ILS <ExternalLink className="w-3 h-3" />
                      </a>
                    </>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">No ILS availability</span>
                  )}
                </div>
              )}
              {group.error ? (
                <div className="p-8 text-center text-sm text-rose-600 flex items-center justify-center gap-2" data-testid={`se-error-${group.part_number}`}>
                  <AlertTriangle className="w-4 h-4" /> {group.error}
                </div>
              ) : group.items.length === 0 ? (
                <div className="p-8 text-center" data-testid={`se-no-results-${group.part_number}`}>
                  <PackageX className="w-6 h-6 mx-auto text-gray-300" />
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-gray-400">No listings found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-summit-canvas">
                        {["Part Number", "Description", "CC", "Qty", "Unit Price", "Company", "Class", "Location", "Contact", "Updated"].map((h) => (
                          <th key={h} className="text-left px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-500 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {group.items.map((it, i) => (
                        <tr key={i} className="border-t border-summit-hairline hover:bg-summit-glacier/30" data-testid={`se-row-${group.part_number}-${i}`}>
                          <td className="px-3 py-2.5 font-mono text-xs font-semibold text-summit-ink whitespace-nowrap">
                            {it.part_number}
                            {it.alternate_part_number && <div className="text-[10px] text-gray-400 font-normal">alt {it.alternate_part_number}</div>}
                          </td>
                          <td className="px-3 py-2.5 text-xs max-w-[220px]">{it.description || <span className="text-gray-300">—</span>}</td>
                          <td className="px-3 py-2.5"><ConditionChip code={it.condition} /></td>
                          <td className="px-3 py-2.5 font-mono text-xs">{it.quantity || "—"}</td>
                          <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{fmtPrice(it.unit_price, it.currency) || <span className="text-gray-300">RFQ</span>}</td>
                          <td className="px-3 py-2.5 text-xs font-medium max-w-[200px]">{it.company}</td>
                          <td className="px-3 py-2.5 font-mono text-[10px] text-gray-500">{it.industry_class || "—"}</td>
                          <td className="px-3 py-2.5 text-xs whitespace-nowrap">{it.location || it.country || "—"}</td>
                          <td className="px-3 py-2.5 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              {it.phone && (
                                <a href={`tel:${it.phone}`} className="text-summit-primary hover:text-summit-primary-soft" title={it.phone} data-testid={`se-phone-${group.part_number}-${i}`}>
                                  <Phone className="w-3.5 h-3.5" />
                                </a>
                              )}
                              {it.email && (
                                <a href={`mailto:${it.email}?subject=RFQ ${encodeURIComponent(it.part_number)}`} className="text-summit-primary hover:text-summit-primary-soft" title={it.email} data-testid={`se-email-${group.part_number}-${i}`}>
                                  <Mail className="w-3.5 h-3.5" />
                                </a>
                              )}
                              {!it.phone && !it.email && <span className="text-gray-300">—</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[10px] text-gray-400 whitespace-nowrap">{it.updated || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
